import { TFunction } from 'i18next';

/**
 * How long points last, on the browser's side.
 *
 * ⚠️ THE MIRROR OF `server/src/utils/pointsExpiry.ts`. The server is
 * authoritative — it refuses a window outside these bounds and it is what
 * actually stamps the date onto the ledger. This copy exists so the host is
 * told while they are typing rather than by a rejected save, and so a buyer can
 * be shown the terms before they pay. Change one, change the other.
 */

export type PointsExpiryUnit = 'days' | 'months';

export const POINTS_EXPIRY_LIMITS: Record<PointsExpiryUnit, { min: number; max: number }> = {
  days: { min: 1, max: 3650 },
  months: { min: 1, max: 120 },
};

/** Both halves or neither. Both absent is the answer "they never expire". */
export const isValidPointsExpiry = (
  value: number | null | undefined,
  unit: string | null | undefined
): boolean => {
  if (value == null && unit == null) return true;
  if (value == null || unit == null) return false;
  if (unit !== 'days' && unit !== 'months') return false;
  if (!Number.isInteger(value)) return false;
  const { min, max } = POINTS_EXPIRY_LIMITS[unit];
  return value >= min && value <= max;
};

/**
 * "Valid for 6 months after purchase", or null when nothing expires.
 *
 * Null rather than "never expires" so the caller decides whether the absence is
 * worth a line at all. On a shop shelf it is not — every pack that has ever
 * been sold said nothing, and a row of "never expires" labels invites the
 * question it answers.
 */
export const describePointsExpiry = (
  t: TFunction,
  value: number | null | undefined,
  unit: string | null | undefined
): string | null => {
  if (!isValidPointsExpiry(value ?? null, unit ?? null)) return null;
  if (value == null || unit == null) return null;

  /**
   * `common`, not `shop` or `products`. This one string is rendered by the
   * HOST's catalog list and by the GUEST's shop and payment screen, and those
   * two never load the same namespaces — `common` is the only one both always
   * have, so the label cannot fall back to English on one of them.
   */
  return unit === 'days'
    ? t('common:points.validForDays', 'Valid for {{count}} days after purchase', {
        count: value,
      })
    : t('common:points.validForMonths', 'Valid for {{count}} months after purchase', {
        count: value,
      });
};

/**
 * The date points bought right now would lapse on — the preview under the
 * host's own input, so the rule they are setting is a date they can picture
 * rather than an arithmetic problem.
 *
 * Deliberately the same month-clamping the server does: a window set on 31
 * January must not preview as 3 March and then save as 28 February.
 */
export const previewPointsExpiry = (
  value: number | null | undefined,
  unit: string | null | undefined,
  from: Date = new Date()
): Date | null => {
  if (!isValidPointsExpiry(value ?? null, unit ?? null)) return null;
  if (value == null || unit == null) return null;

  const result = new Date(from.getTime());
  if (unit === 'days') {
    result.setDate(result.getDate() + value);
    return result;
  }

  const targetMonth = result.getMonth() + value;
  const dayOfMonth = result.getDate();
  result.setDate(1);
  result.setMonth(targetMonth);
  // Last day of the target month, so 31 January + 1 month lands on 28 February
  // rather than spilling into March.
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(dayOfMonth, lastDay));
  return result;
};
